/**
 * Helpers for rendering an ability/skill/agent icon.
 *
 * The `metadata.icon` field is a string with no schema-level discrimination:
 * sometimes it's an emoji ("📑"), sometimes a single letter ("C"), and
 * sometimes a hosted image URL ("https://github.com/ComposioHQ.png" for
 * marketplace abilities imported with a brand logo). Components used to
 * render the value as raw text, which surfaced URLs verbatim in install
 * dialogs and grid cards. These helpers let every render site pick the
 * right branch (image vs glyph) consistently.
 */

/**
 * True when the icon string should be rendered as an `<img src>` rather than
 * a text node. Covers absolute http(s) URLs and site-root absolute paths.
 */
export function isIconUrl(icon: string | null | undefined): icon is string {
  if (!icon) return false;
  return /^(https?:\/\/|\/)/.test(icon);
}

/**
 * First grapheme of a string with emoji-safe handling so multi-codepoint
 * glyphs (flags, ZWJ sequences) render as a single mark. Useful as a
 * fallback initial when no icon is set.
 */
export function firstGlyph(s: string | null | undefined): string {
  if (!s) return "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Seg = (Intl as any)?.Segmenter;
  if (typeof Seg === "function") {
    const seg = new Seg(undefined, { granularity: "grapheme" });
    const it = seg.segment(s)[Symbol.iterator]();
    const first = it.next();
    return first.done ? "" : first.value.segment;
  }
  return Array.from(s)[0] ?? "";
}
